import React, { useEffect, useState } from "react";
import { ChevronDown, Plus, X, Trash } from "lucide-react";
import Header from "../Common/AdminHeader";
import axios from "axios";
import { Config } from "../../../API/config";
import toast from "react-hot-toast"; // Added toast import

interface SubCategory {
    _id: string;
    name: string;
    categoryId: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

interface Category {
    _id: string;
    name: string;
    subCategories: SubCategory[];
    createdAt: string;
    updatedAt: string;
    __v: number;
}

const MangeCategories = () => {
    const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});
    const [isSubCategoryModalOpen, setIsSubCategoryModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [newSubCategory, setNewSubCategory] = useState("");
    const [newCategory, setNewCategory] = useState("");
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            let { data } = await axios.get(Config.getAllRegisterCategory);
            if (data.statusCode === 200 && Array.isArray(data.data)) {
                setCategories(data.data);
            } else {
                setCategories([]);
            }
        } catch (err) {
            console.error("Error fetching categories", err);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const downloadCategoriesExcelSheet = async () => {
        try {
            const response = await axios.get('https://otlobcoach.com/api/v1/category/getAllCategoriesSheet', {
                withCredentials: true,
                responseType: 'blob', // Handle binary Excel file
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Categories.xlsx'); // Filename from content-disposition
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success("تم تحميل ملف Excel للفئات بنجاح");
        } catch (err) {
            console.error("Error downloading categories Excel sheet:", err);
            toast.error("حدث خطأ أثناء تحميل ملف Excel للفئات");
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const toggleCategory = (categoryId: string) => {
        setOpenCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const openSubCategoryModal = (categoryId: string) => {
        setSelectedCategoryId(categoryId);
        setIsSubCategoryModalOpen(true);
    };

    const addSubCategory = async () => {
        if (newSubCategory.trim() !== "" && selectedCategoryId) {
            setLoading(true);
            try {
                let response = await axios.post(`${Config.addNewSubCategory}/${selectedCategoryId}`, {
                    name: newSubCategory.trimStart().trimEnd(),
                }, { withCredentials: true });
                if (response.data.statusCode === 201) {
                    fetchCategories();
                    toast.success("تم إضافة الفئة الفرعية بنجاح");
                }
            } catch (err) {
                console.error("Error adding subcategory", err);
                toast.error("حدث خطأ أثناء إضافة الفئة الفرعية");
            } finally {
                setLoading(false);
                setIsSubCategoryModalOpen(false);
                setNewSubCategory("");
            }
        }
    };

    const addCategory = async () => {
        if (newCategory.trim() !== "") {
            setLoading(true);
            try {
                let response = await axios.post(Config.addNewCategory, {
                    name: newCategory.trimStart().trimEnd(),
                }, { withCredentials: true });
                if (response.data.statusCode === 201) {
                    fetchCategories();
                    toast.success("تم إضافة الفئة بنجاح");
                }
            } catch (err) {
                console.error("Error adding category", err);
                toast.error("حدث خطأ أثناء إضافة الفئة");
            } finally {
                setLoading(false);
                setIsCategoryModalOpen(false);
                setNewCategory("");
            }
        }
    };

    const deleteCategory = async (categoryId: string) => {
        if (window.confirm("هل أنت متأكد أنك تريد حذف هذه الفئة؟")) {
            setLoading(true);
            try {
                let response = await axios.delete(`${Config.deleteCategory}/${categoryId}`, { withCredentials: true });
                if (response.data.statusCode === 200) {
                    fetchCategories();
                    toast.success("تم حذف الفئة بنجاح");
                }
            } catch (err) {
                console.error("Error deleting category", err);
                toast.error("حدث خطأ أثناء حذف الفئة");
            } finally {
                setLoading(false);
            }
        }
    };

    const deleteSubCategory = async (categoryId: string, subCategoryId: string) => {
        if (window.confirm("هل أنت متأكد أنك تريد حذف هذه الفئة الفرعية؟")) {
            setLoading(true);
            try {
                let response = await axios.delete(`${Config.deleteSubCategory}/${subCategoryId}`, { withCredentials: true });
                if (response.data.statusCode === 200) {
                    fetchCategories();
                    toast.success("تم حذف الفئة الفرعية بنجاح");
                }
            } catch (err) {
                console.error("Error deleting subcategory", err);
                toast.error("حدث خطأ أثناء حذف الفئة الفرعية");
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="flex-1 overflow-auto relative z-10 bg-white" dir="rtl">
            <Header title="إدارة الفئات " />
            <main className="max-w-full md:max-w-7xl mx-auto md:py-6 md:px-4 lg:px-8 overflow-auto px-2 sm:px-4">
                <div className="p-4 border rounded-md w-full bg-white shadow-md">
                    <div className="flex flex-col md:flex-row justify-between items-center border-b pb-2 mb-2">
                        <h2 className="text-lg font-bold text-center md:text-right w-full md:w-auto mt-2 md:mt-0">
                            الفئات والتنظيم
                        </h2>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setIsCategoryModalOpen(true)}
                                className="bg-green-800 text-white px-4 py-2 rounded-md flex items-center space-x-reverse space-x-2"
                            >
                                <Plus size={16} className="mr-4" />
                                <span>اضف فئة</span>
                            </button>
                            <button
                                onClick={downloadCategoriesExcelSheet}
                                className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center space-x-reverse space-x-2"
                            >
                                <span>تحميل ملف Excel</span>
                            </button>
                        </div>
                    </div>
                    {loading ? (
                        <div className="text-center py-4">جاري التحميل...</div>
                    ) : categories.length > 0 ? (
                        <ul>
                            {categories.map((category) => (
                                <li key={category._id} className="mb-2">
                                    <div className="flex justify-between items-center cursor-pointer p-2 hover:bg-gray-100 rounded-md">
                                        <div className="flex items-center gap-2">
                                            <Plus size={16} className="text-gray-500" onClick={(e) => {
                                                e.stopPropagation();
                                                openSubCategoryModal(category._id);
                                            }} />
                                            <span onClick={() => toggleCategory(category._id)}>{category.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {category.subCategories.length > 0 && (
                                                <ChevronDown
                                                    className={`transition-transform cursor-pointer ${openCategories[category._id] ? "rotate-180" : ""}`}
                                                    onClick={() => toggleCategory(category._id)}
                                                />
                                            )}
                                            <Trash size={16} className="text-red-500 cursor-pointer" onClick={() => deleteCategory(category._id)} />
                                        </div>
                                    </div>
                                    {category.subCategories.length > 0 && openCategories[category._id] && (
                                        <ul className="pr-6 mt-1 border-r">
                                            {category.subCategories.map((sub) => (
                                                <li key={sub._id} className="flex justify-between items-center p-2 my-1 bg-gray-50 text-sm md:text-base">
                                                    <span>{sub.name}</span>
                                                    <Trash size={14} className="text-red-500 cursor-pointer" onClick={() => deleteSubCategory(category._id, sub._id)} />
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-center py-4 text-gray-500">لا توجد فئات متاحة</div>
                    )}
                </div>
            </main>

            {/* Add Category Modal */}
            {isCategoryModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
                    <div className="bg-white p-4 rounded-md shadow-md w-96">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold">إضافة فئة جديدة</h2>
                            <X className="cursor-pointer" onClick={() => setIsCategoryModalOpen(false)} />
                        </div>
                        <input
                            type="text"
                            className="w-full border p-2 rounded-md mb-4"
                            placeholder="اسم الفئة الجديدة"
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            disabled={loading}
                        />
                        <button
                            className="bg-green-900 text-white px-4 py-2 rounded-md w-full"
                            onClick={addCategory}
                            disabled={loading}
                        >
                            {loading ? "جاري الإضافة..." : "إضافة"}
                        </button>
                    </div>
                </div>
            )}

            {/* Add SubCategory Modal */}
            {isSubCategoryModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
                    <div className="bg-white p-4 rounded-md shadow-md w-96">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold">إضافة فئة فرعية</h2>
                            <X className="cursor-pointer" onClick={() => setIsSubCategoryModalOpen(false)} />
                        </div>
                        <input
                            type="text"
                            className="w-full border p-2 rounded-md mb-4"
                            placeholder="اسم الفئة الفرعية الجديدة"
                            value={newSubCategory}
                            onChange={(e) => setNewSubCategory(e.target.value)}
                            disabled={loading}
                        />
                        <button
                            className="bg-green-900 text-white px-4 py-2 rounded-md w-full"
                            onClick={addSubCategory}
                            disabled={loading}
                        >
                            {loading ? "جاري الإضافة..." : "إضافة"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MangeCategories;